// src/pages/SettingsScreen.js

import React, { useState, useEffect } from 'react';
import { 
    Bell, 
    AlertTriangle, 
    CalendarDays, 
    Loader2, 
    Layout,       // For Landing Page
    Building2,    // For Default Property
    Filter,       // For Default View
    Clock         // For Pay Cycle
} from 'lucide-react';
import toast from 'react-hot-toast';
import { subscribeUser } from '../services/push-notifications';
import { api } from '../api';
import '../styles/SettingsScreen.css';

// --- REUSABLE DROPDOWN COMPONENT ---
const DropdownSetting = ({ icon, label, description, value, options, onChange, isLoading }) => {
    return (
        <div className="setting-item">
            <div className="setting-icon">{icon}</div>
            <div className="setting-details">
                <span className="setting-label">{label}</span>
                <p className="setting-description">{description}</p>
            </div>
            <div className="setting-control">
                <select 
                    className="settings-dropdown"
                    value={value} 
                    onChange={(e) => onChange(e.target.value)}
                    disabled={isLoading}
                >
                    {options.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                            {opt.label}
                        </option>
                    ))}
                </select>
            </div>
        </div>
    );
};

// --- NOTIFICATION COMPONENT ---
const NotificationSetting = ({ icon, label, description, channels, onChannelChange, isMasterEnabled }) => {
    return (
        <div className={`setting-item ${!isMasterEnabled ? 'disabled' : ''}`}>
            <div className="setting-icon">{icon}</div>
            <div className="setting-details">
                <span className="setting-label">{label}</span>
                <p className="setting-description">{description}</p>
            </div>
            <div className="channel-controls">
                <label className="channel-checkbox">
                    <input
                        type="checkbox"
                        checked={channels.push}
                        disabled={!isMasterEnabled}
                        onChange={(e) => onChannelChange('push', e.target.checked)}
                    />
                    <span>Push</span>
                </label>
                <label className="channel-checkbox">
                    <input
                        type="checkbox"
                        checked={channels.email}
                        disabled={!isMasterEnabled}
                        onChange={(e) => onChannelChange('email', e.target.checked)}
                    />
                    <span>Email</span>
                </label>
            </div>
        </div>
    );
};

const SettingsScreen = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [notificationsEnabled, setNotificationsEnabled] = useState(false);

    // --- NOTIFICATION STATE ---
    const [notifyOnMissing, setNotifyOnMissing] = useState({ push: false, email: false });
    const [notifyOnPayroll, setNotifyOnPayroll] = useState({ push: false, email: false });

    // --- UI PREFERENCE STATE ---
    const [uiPrefs, setUiPrefs] = useState({
        default_landing_page: 'dashboard',
        default_property_id: '', 
        default_report_view: 'today',    
        payroll_frequency: 'semimonthly' 
    });

    const [availableProperties, setAvailableProperties] = useState([]);

    useEffect(() => {
        if ('Notification' in window && Notification.permission === 'granted') {
            setNotificationsEnabled(true);
        }

        const loadData = async () => {
            try {
                // Fetch prefs and properties in parallel
                const [savedPreferences, propertiesData] = await Promise.all([
                    api.getUserPreferences(),
                    api.getProperties() 
                ]);

                setAvailableProperties(propertiesData || []);

                const getJsonPref = (key) => {
                    const pref = savedPreferences.find(p => p.preference_key === key);
                    if (!pref || !pref.preference_value) return { push: false, email: false };
                    try {
                        return pref.preference_value === 'true' 
                            ? { push: true, email: false } 
                            : JSON.parse(pref.preference_value);
                    } catch (e) { return { push: false, email: false }; }
                };

                const getStringPref = (key, fallback) => {
                    const pref = savedPreferences.find(p => p.preference_key === key);
                    return pref ? pref.preference_value : fallback;
                };

                setNotifyOnMissing(getJsonPref('notify_on_missing'));
                setNotifyOnPayroll(getJsonPref('notify_on_payroll'));
                
                setUiPrefs({
                    default_landing_page: getStringPref('default_landing_page', 'dashboard'),
                    default_property_id: getStringPref('default_property_id', ''),
                    default_report_view: getStringPref('default_report_view', 'today'), 
                    payroll_frequency: getStringPref('payroll_frequency', 'semimonthly'), 
                });

            } catch (error) {
                toast.error("Could not load your settings.");
            } finally {
                setIsLoading(false);
            }
        };

        loadData();
    }, []);

    const handleUiPrefChange = async (key, value) => {
        setUiPrefs(prev => ({ ...prev, [key]: value }));
        try {
            await api.updateUserPreferences({ [key]: value });
            toast.success('Setting saved');
        } catch (error) {
            toast.error('Failed to save setting');
        }
    };

    const handleNotificationChange = async (setter, currentChannels, preferenceKey, channel, isEnabled) => {
        if (!notificationsEnabled) {
            toast.error("Please enable the master notification setting first.");
            return;
        }
        const newChannels = { ...currentChannels, [channel]: isEnabled };
        setter(newChannels);
        try {
            await api.updateUserPreferences({ [preferenceKey]: JSON.stringify(newChannels) });
            toast.success('Preference updated!');
        } catch (error) {
            toast.error('Could not save preference.');
            setter(currentChannels);
        }
    };

    const handleMasterToggle = async () => {
        if (!notificationsEnabled) {
             try {
                await subscribeUser();
                setNotificationsEnabled(true);
                toast.success('Push notifications enabled!');
            } catch (error) {
                setNotificationsEnabled(false);
                toast.error('Failed to enable notifications.');
            }
        } else {
            setNotificationsEnabled(false);
        }
    };
    
    if (isLoading) return <div className="loading-screen"><Loader2 className="spin" /></div>;

    return (
        <div className="settings-page-container">
            <div className="page-header">
                <h1>Settings</h1>
            </div>

            {/* --- APPLICATION PREFERENCES --- */}
            <div className="card settings-card">
                <h2 className="section-title">Application Preferences</h2>
                <p className="section-description">Customize your default views and behavior.</p>

                <DropdownSetting 
                    icon={<Layout size={24} />}
                    label="Default Landing Page"
                    description="Which screen should open when you log in?"
                    value={uiPrefs.default_landing_page}
                    onChange={(val) => handleUiPrefChange('default_landing_page', val)}
                    options={[
                        { value: 'dashboard', label: 'Dashboard' },
                        { value: 'reports', label: 'Reports' },
                        { value: 'properties', label: 'Properties' },
                    ]}
                />

                <DropdownSetting 
                    icon={<Building2 size={24} />}
                    label="Default Property"
                    description="The property automatically selected in dropdowns."
                    value={uiPrefs.default_property_id}
                    onChange={(val) => handleUiPrefChange('default_property_id', val)}
                    options={[
                        { value: '', label: 'Select First Available (Default)' },
                        ...availableProperties.map(p => ({ value: p.id, label: p.name }))
                    ]}
                />

                {/* --- SPLIT SETTINGS BELOW --- */}

                <DropdownSetting 
                    icon={<Clock size={24} />}
                    label="Payroll Cycle"
                    description="Future setting for billing cycles."
                    value={uiPrefs.payroll_frequency}
                    onChange={(val) => handleUiPrefChange('payroll_frequency', val)}
                    options={[
                        { value: 'semimonthly', label: 'Twice a Month (1-15 & 16-End)' },
                        { value: 'monthly', label: 'Monthly' },
                    ]}
                />

                <DropdownSetting 
                    icon={<Filter size={24} />}
                    label="Default Report View"
                    description="Which date tab should be selected by default?"
                    value={uiPrefs.default_report_view}
                    onChange={(val) => handleUiPrefChange('default_report_view', val)}
                    options={[
                        { value: 'today', label: 'Today' },
                        { value: 'twice_a_month', label: 'Twice a Month (Auto Select)' },
                        { value: 'month', label: 'This Month' },
                    ]}
                />
            </div>

            {/* --- NOTIFICATIONS (UNCHANGED) --- */}
            <div className="card settings-card">
                <h2 className="section-title">Notifications</h2>
                <div className={`setting-item`}>
                    <div className="setting-icon"><Bell size={24} /></div>
                    <div className="setting-details">
                        <span className="setting-label">Enable Push Notifications</span>
                        <p className="setting-description">Allow this device to receive notifications.</p>
                    </div>
                    <div className="setting-control">
                        <label className="toggle-switch">
                            <input type="checkbox" checked={notificationsEnabled} onChange={handleMasterToggle} />
                            <span className="slider"></span>
                        </label>
                    </div>
                </div>

                <div className={`sub-section ${!notificationsEnabled ? 'disabled-section' : ''}`}>
                    <NotificationSetting
                        icon={<AlertTriangle size={24} />}
                        label="Missing Clock-Outs"
                        description="Get an alert when a user forgets to clock out."
                        channels={notifyOnMissing}
                        onChannelChange={(channel, isEnabled) => 
                            handleNotificationChange(setNotifyOnMissing, notifyOnMissing, 'notify_on_missing', channel, isEnabled)}
                        isMasterEnabled={notificationsEnabled}
                    />

                    <NotificationSetting
                        icon={<CalendarDays size={24} />}
                        label="Payroll Reminders"
                        description="Receive reminders on the 15th and end of the month."
                        channels={notifyOnPayroll}
                        onChannelChange={(channel, isEnabled) => 
                            handleNotificationChange(setNotifyOnPayroll, notifyOnPayroll, 'notify_on_payroll', channel, isEnabled)}
                        isMasterEnabled={notificationsEnabled}
                    />
                </div>
            </div>
        </div>
    );
};

export default SettingsScreen;